import type { Tier } from "../components/ui/tier-badge";
import type {
  ReadinessFactorID,
  ReadinessSegment,
} from "./readiness-types";

export type SillScoreBand = "getting-ready" | "on-track" | "ready" | "approved";
export type { Tier };

export type ReadinessCopyMarketplaceState = "pre" | "post";

export interface ReadinessCopyKey {
  band: SillScoreBand;
  tier: Tier;
  marketplaceState: ReadinessCopyMarketplaceState;
  // SILL-323 follow-up: segment lets the copy table differentiate DNQ
  // ("Not yet qualified") from scored states. When omitted, scored copy
  // is returned (back-compat). DNQ overrides both sharpenHint (no "+N
  // points" framing — would imply movement on a pre-scale headline)
  // and advisorChip (cap-resolution is the path, not band-tightening).
  segment?: ReadinessSegment;
}

export interface ReadinessCopy {
  bandLabel: string;
  sharpenHint: string;
  advisorChip: string;
}

type UnverifiedTier = "self-reported" | "document-backed";

function isUnverified(tier: Tier): tier is UnverifiedTier {
  return tier === "self-reported" || tier === "document-backed";
}

const BAND_LABELS: Record<SillScoreBand, string> = {
  "getting-ready": "Getting ready",
  "on-track": "On track",
  ready: "Ready to buy",
  approved: "Pre-approved",
};

const DNQ_BAND_LABEL = "Not yet qualified";

function sharpenHint(
  band: SillScoreBand,
  tier: Tier,
  segment: ReadinessSegment | undefined,
): string {
  if (segment === "not_yet_qualified") {
    // Pre-scale: verification strengthens the verified profile +
    // marketplace standing, but it doesn't move the headline. Promising
    // "+N points" here contradicts the cap banner on the same card.
    return isUnverified(tier)
      ? "verifying strengthens your profile for lenders"
      : "your profile is verified for lenders";
  }
  if (!isUnverified(tier)) return "your score reflects verified inputs";

  switch (band) {
    case "getting-ready":
      return "two verified inputs would sharpen this by about 12 points";
    case "on-track":
      return "two verified inputs would sharpen this by about 8 points";
    case "ready":
      return "one verified input would sharpen this by about 4 points";
    case "approved":
      return "your score reflects the data on file";
  }
}

function advisorChip(
  band: SillScoreBand,
  tier: Tier,
  marketplaceState: ReadinessCopyMarketplaceState,
  segment: ReadinessSegment | undefined,
): string {
  if (segment === "not_yet_qualified") {
    return marketplaceState === "post"
      ? "Let's clear what's holding the score back so the marketplace opens up."
      : "Let's clear what's holding the score back before the marketplace opens.";
  }
  if (band === "approved") {
    return marketplaceState === "post"
      ? "You're approved — let's find your match on the marketplace."
      : "You're approved — you'll be first in line when the marketplace opens.";
  }

  if (!isUnverified(tier)) {
    return marketplaceState === "post"
      ? "Let's sharpen your readiness further to unlock stronger marketplace offers."
      : "Let's sharpen your readiness further before the marketplace opens.";
  }

  return marketplaceState === "post"
    ? "Let's sharpen your readiness score to unlock stronger marketplace offers."
    : "Let's sharpen your readiness score so you're first in line when the marketplace opens.";
}

// ── DNQ (not-yet-qualified) presentation copy ────────────────────────
//
// SILL-418/419 (chat-door graceful-DNQ): a `not_yet_qualified` profile has
// a null score and renders a bare em-dash today. This copy system replaces
// the dead-end with a warm, honest "you're getting started" framing plus a
// next step mapped from the engine's `cap_reason_detail`. Every line is
// HONEST — it names the gap without ever implying the household qualified
// or was approved (the chat-door's whole reason for existing).
//
// D4: panels MUST read this — no DNQ string literals live in the render
// surfaces (enforced by score-panel-guard.test.ts). The headline leads
// with progress, the nextStep leads with the household's own next move,
// and panels lead the overall presentation with the buying-power hero +
// the top goal (goals-led framing) sourced from the readiness result.

export interface DnqCopy {
  // Progress-framed headline — "You're getting started", never a failure
  // verdict. Pairs with the score placeholder so the card reads as a
  // beginning, not a rejection.
  headline: string;
  // One warm, forward-looking next step mapped from cap_reason_detail.
  // Names the gap honestly; never promises qualification.
  nextStep: string;
}

const DNQ_HEADLINE = "You're getting started";

// Ordered matchers against the backend's `cap_reason_detail` strings
// (readiness/plan_viability.go: dnqDetail + planUnderfundedDetail). Each
// returns a warm next step. Order matters only for overlapping substrings;
// the variants below are mutually exclusive in practice. Keep this in sync
// with the Go source — a new gate string falls through to the generic line.
const DNQ_NEXT_STEP_MATCHERS: ReadonlyArray<{
  match: (detail: string) => boolean;
  nextStep: string;
}> = [
  {
    match: (d) => /no income/i.test(d),
    nextStep:
      "Add your household income so we can size up what you can borrow.",
  },
  {
    match: (d) => /\bdti\b|over 50%/i.test(d),
    nextStep:
      "Trimming your monthly debts frees up room in your budget — let's start there.",
  },
  {
    match: (d) => /credit/i.test(d),
    nextStep:
      "Adding your credit details lets us tailor the next steps to your situation.",
  },
  {
    match: (d) => /employ/i.test(d),
    nextStep:
      "A steadier work history strengthens your file — we'll watch it grow with you.",
  },
  {
    match: (d) => /need ~?\$|underfunded|insufficient cash|for target/i.test(d),
    nextStep:
      "Growing your cash on hand is the next move — small, steady savings add up fast.",
  },
];

const DNQ_GENERIC_NEXT_STEP =
  "Let's keep building your profile — every detail you add sharpens your plan.";

// getDnqCopy maps the engine's `cap_reason_detail` to a warm, honest DNQ
// presentation. `undefined`/empty/unknown details fall back to the generic
// next step. NEVER returns "qualified"/"approved" language.
export function getDnqCopy(capReasonDetail: string | undefined): DnqCopy {
  const detail = capReasonDetail ?? "";
  const matched = DNQ_NEXT_STEP_MATCHERS.find((m) => m.match(detail));
  return {
    headline: DNQ_HEADLINE,
    nextStep: matched ? matched.nextStep : DNQ_GENERIC_NEXT_STEP,
  };
}

// getDnqAffordabilityMove returns the honest "lower your target" lead for an
// affordability-driven DNQ — the case where the engine's real BuyingPowerMax
// sits below the target price. SILL-418 handoff (2026-06-22): when a
// household's actual buying power is well under their target, "pay down debt"
// is rarely the achievable lever (a 35%-DTI-before-mortgage buyer can't trim
// their way to a $900K home). The honest first move is a lower target — which
// the "Change target" affordance right above this line already supports — or
// more income. `buyingPowerText` is the engine BuyingPowerMax, already
// formatted (e.g. "$418K"). Uses budget/reach framing, NEVER "qualified"/
// "approved" — the chat-door graceful-DNQ promise holds here too.
export function getDnqAffordabilityMove(buyingPowerText: string): string {
  return `Right now your budget reaches homes around ${buyingPowerText} — consider lowering your target, or growing income to aim higher.`;
}

export function getReadinessCopy(key: ReadinessCopyKey): ReadinessCopy {
  const { band, tier, marketplaceState, segment } = key;
  return {
    bandLabel:
      segment === "not_yet_qualified" ? DNQ_BAND_LABEL : BAND_LABELS[band],
    sharpenHint: sharpenHint(band, tier, segment),
    advisorChip: advisorChip(band, tier, marketplaceState, segment),
  };
}

function collectPhrases(): string[] {
  const phrases = new Set<string>();
  const bands: SillScoreBand[] = [
    "getting-ready",
    "on-track",
    "ready",
    "approved",
  ];
  const tiers: Tier[] = [
    "self-reported",
    "document-backed",
    "bank-verified",
    "lender-approved",
  ];
  const states: ReadinessCopyMarketplaceState[] = ["pre", "post"];
  const segments: (ReadinessSegment | undefined)[] = [
    undefined,
    "not_yet_qualified",
    "getting_close",
    "ready_to_act",
  ];
  for (const band of bands) {
    for (const tier of tiers) {
      for (const marketplaceState of states) {
        for (const segment of segments) {
          const copy = getReadinessCopy({
            band,
            tier,
            marketplaceState,
            segment,
          });
          phrases.add(copy.bandLabel);
          phrases.add(copy.sharpenHint);
          phrases.add(copy.advisorChip);
        }
      }
    }
  }
  return [...phrases];
}

export const READINESS_COPY_GUARDED_PHRASES: readonly string[] =
  collectPhrases();

// factorShortCopy is per-row chip copy used by the SILL-187 Profile page's
// per-row meta line and SharpenPill. It is intentionally NOT included in
// READINESS_COPY_GUARDED_PHRASES — the guarded-phrase set governs the
// score panel's narrative copy (band, sharpen hint, marketplace gate),
// while factorShortCopy is action-label scaffolding that the readiness
// engine's goals[] entries override at render time.
const FACTOR_SHORT_COPY: Record<ReadinessFactorID, string> = {
  buying_power: "Stretch your buying power",
  down_payment: "Grow down payment",
  cash_execution: "Fund cash to close",
  credit: "Lift your credit",
  dti: "Trim monthly debts",
  timeline: "Tighten your timeline",
  stability: "Stabilize housing",
  work_history: "Lengthen work history",
  profile_completeness: "Complete your profile",
};

export function factorShortCopy(id: ReadinessFactorID): string {
  return FACTOR_SHORT_COPY[id];
}
